import { useState, useEffect } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gauge, Loader2 } from "lucide-react";

const DEFAULT_BG = "https://images.unsplash.com/photo-1618312980096-873bd19759a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bg, setBg] = useState(DEFAULT_BG);
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/branding`)
      .then((r) => r.json())
      .then((d) => { if (d?.login_background) setBg(d.login_background); })
      .catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password, remember);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="grid min-h-screen w-full lg:grid-cols-2">
      <div className="relative hidden min-h-screen lg:block">
        <img src={bg} alt="Autoservis" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="absolute inset-y-0 left-0 z-10 flex w-full flex-col justify-center px-10">
          <div className="max-w-md rounded-xl bg-black/55 p-5 backdrop-blur-sm">
            <h2 className="font-heading text-4xl font-extrabold uppercase tracking-wide text-white">Správa autoservisu<br/>na jednom mieste</h2>
            <p className="mt-3 text-white/85">Zákazky, vozidlá, zákazníci a faktúry s DPH a QR platbou PAY by square.</p>
          </div>
        </div>
      </div>

      <div className="flex min-h-screen flex-col items-center justify-center p-8">
        <form onSubmit={submit} className="mx-auto w-full max-w-sm space-y-4" data-testid="login-form">
          <div className="mb-2 flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground"><Gauge className="h-6 w-6" /></div>
            <div>
              <div className="font-heading text-2xl font-extrabold uppercase tracking-wide">APIS</div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Manager</div>
            </div>
          </div>
          <div>
            <h1 className="font-heading text-3xl font-extrabold uppercase">Prihlásenie</h1>
            <p className="mt-1 text-sm text-muted-foreground">Zadajte prihlasovacie údaje</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="min-h-[48px]" data-testid="login-email-input" placeholder="vas@email.sk" />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Heslo</Label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Zabudnuté heslo?</Link>
            </div>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="min-h-[48px]" data-testid="login-password-input" placeholder="••••••••" />
          </div>
          {error && <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="login-error">{error}</div>}
          <label className="flex cursor-pointer select-none items-center gap-2 text-sm text-muted-foreground">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-primary" data-testid="login-remember-checkbox" />
            Zapamätať prihlásenie na tomto zariadení
          </label>
          <Button type="submit" className="min-h-[48px] w-full text-base font-semibold" disabled={loading} data-testid="login-submit-button">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Prihlásiť sa"}
          </Button>
          <p className="text-center text-sm text-muted-foreground">Nemáte účet? <Link to="/register" className="text-primary hover:underline" data-testid="to-register-link">Registrovať sa</Link></p>
          <p className="text-center text-xs text-muted-foreground">
            <Link to="/obchodne-podmienky" className="hover:text-foreground hover:underline" data-testid="login-terms-link">Obchodné podmienky</Link>
            {" · "}
            <Link to="/ochrana-osobnych-udajov" className="hover:text-foreground hover:underline" data-testid="login-privacy-link">Ochrana osobných údajov</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
